import React, { useEffect, useState } from 'react';
import { useAuth } from './contexts/AuthContext';
import { useImpersonation } from './contexts/ImpersonationContext';
import { LoginPage } from './components/LoginPage';
import App from './App';
import PortalApp from './pages/portal/PortalApp';
import SuperAdminApp from './pages/superadmin/SuperAdminApp';
import PublicIntakeForm from './pages/PublicIntakeForm';
import InvoicePublicPage from './pages/InvoicePublicPage';
import PortalLoginPage from './pages/PortalLoginPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import MarketingHomePage from './pages/marketing/MarketingHomePage';
import BookDemoPage      from './pages/marketing/BookDemoPage';
import AboutPage         from './pages/marketing/AboutPage';
import PricingPage       from './pages/marketing/PricingPage';
import SecurityPage      from './pages/marketing/SecurityPage';
import MarketingNav      from './pages/marketing/MarketingNav';
import MarketingFooter   from './pages/marketing/MarketingFooter';
import WatchDemoModal    from './pages/marketing/WatchDemoModal';
import { parseMarketingRoute } from './pages/marketing/marketingTokens';
import { joinFirmByInviteCode, fetchPortalContact } from './lib/db';
import { supabase } from './services/supabaseClient';
import { Loader, KeyRound, AlertCircle, CheckCircle, UserX } from 'lucide-react';

// Detect #/intake/:id in the URL hash — works for both /#/intake/ID and #/intake/ID
function getPublicIntakeId() {
  const hash = window.location.hash || '';
  const match = hash.match(/[#/]intake\/([a-zA-Z0-9_-]+)/);
  return match ? match[1] : null;
}

function getPublicInvoiceToken() {
  const hash = window.location.hash || '';
  const match = hash.match(/[#/]invoice\/([a-f0-9-]{36})/);
  return match ? match[1] : null;
}

// Detect /#/portal-invite/... and /#/portal-login — show client login page
function isPortalLoginRoute() {
  const hash = window.location.hash || '';
  return /[#/](portal-invite|portal-login)(\/|$)/.test(hash);
}

// ─── Marketing surface router ───────────────────────────────────────
// Routes for the unauthenticated visitor:
//   #/             → MarketingHomePage  (with nav + footer)
//   #/about        → AboutPage          (with nav + footer)
//   #/book-demo    → BookDemoPage       (with nav + footer)
//   #/login,#/signup → existing LoginPage (no nav/footer; it has its own chrome)
//   anything else  → MarketingHomePage  (catch-all, never dead-ends a visitor)
//
// We pin the hash to a state value so the component re-renders on
// hashchange. The actual route detection lives in marketingTokens.js
// so it's unit-testable.
function MarketingRouter() {
  const [hash, setHash] = useState(window.location.hash);

  useEffect(() => {
    const onHashChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const route = parseMarketingRoute(hash);

  // Login / signup get the original full-page LoginPage with no
  // marketing chrome — it has its own two-panel design.
  if (route === 'login' || route === 'signup') return <LoginPage />;

  // Everything else gets the marketing chrome (nav + footer).
  const Page =
    route === 'about'    ? AboutPage
    : route === 'pricing'  ? PricingPage
    : route === 'security' ? SecurityPage
    : route === 'bookDemo' ? BookDemoPage
    : MarketingHomePage;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#FFFFFF' }}>
      <MarketingNav activeRoute={route} />
      <div className="flex-1">
        <Page />
      </div>
      <MarketingFooter />
      {/* Global "Watch demo" modal. Listens for the
          `docketflow:openDemo` window event — any button anywhere on
          the marketing surface can open it via <WatchDemoButton />. */}
      <WatchDemoModal />
    </div>
  );
}

// ── Password setup screen — shown to invited users before they enter the app ──
function SetPasswordScreen() {
  const { completePasswordSetup } = useAuth();
  const [pw,           setPw]          = useState('');
  const [confirm,      setConfirm]     = useState('');
  const [saving,       setSaving]      = useState(false);
  const [error,        setError]       = useState('');
  const [done,         setDone]        = useState(false);
  const [sessionReady, setSessionReady]= useState(false);
  // verifyError is the "the invite link itself didn't work" state —
  // distinct from `error` which is "your password isn't valid". Set
  // either when Supabase emits an explicit failure event or when we
  // time out waiting for SIGNED_IN. Surfaces an actionable retry /
  // request-new-invite UI instead of leaving the user staring at a
  // spinner forever.
  const [verifyError, setVerifyError] = useState('');

  // Wait for Supabase to finish exchanging the invite token for a live
  // session. There are THREE incoming link shapes to handle:
  //
  //   1. Implicit flow (preferred):
  //         /#access_token=eyJ...&refresh_token=...&type=invite
  //      detectSessionInUrl processes the hash and emits SIGNED_IN.
  //      Works on any device — no localStorage state needed.
  //
  //   2. PKCE flow (legacy / broken for cross-device invites):
  //         /?code=XXXXX&type=invite
  //      Needs a code_verifier in localStorage that only exists on the
  //      sender's device. Will silently fail for an email-and-click
  //      flow. We've switched supabaseClient to flowType:'implicit' to
  //      stop generating these going forward, but already-sent invites
  //      may still arrive in this shape.
  //
  //   3. token_hash flow (Supabase's newer email link shape):
  //         /?token_hash=XXX&type=invite
  //      Requires an explicit supabase.auth.verifyOtp() call —
  //      detectSessionInUrl will NOT auto-process this. We handle it
  //      manually below so the link Just Works regardless of which
  //      format Supabase chose to emit.
  //
  // Three safety nets on the wait:
  //   * 20-second timeout — surface an actionable error before the
  //     user gives up.
  //   * Listen for SIGNED_OUT / USER_DELETED during the wait — these
  //     mean Supabase definitively rejected the token.
  //   * Explicit verifyOtp() for the token_hash shape.
  useEffect(() => {
    let resolved = false;
    let subscription;

    const finish = (ok, errMsg) => {
      if (resolved) return;
      resolved = true;
      if (ok) setSessionReady(true);
      else setVerifyError(errMsg);
      if (subscription) subscription.unsubscribe();
    };

    // 20-second timeout — Supabase auth normally exchanges within 1-2s
    // when the project is warm. 20s gives plenty of headroom for cold
    // starts + slow networks while still recovering before the user
    // gives up entirely.
    const timeout = setTimeout(() => {
      finish(false,
        'We couldn\'t verify your invite link. It may have expired (links are valid for 24 hours) '
        + 'or already been used. Ask your firm to send a new invite.'
      );
    }, 20_000);

    // ── (3) Explicit token_hash exchange ────────────────────────────
    // Look at BOTH the query string and the URL fragment — Supabase
    // has been inconsistent about which it uses across SDK versions.
    const queryParams = new URLSearchParams(window.location.search);
    const fragParams  = new URLSearchParams(window.location.hash.replace(/^#\/?/, ''));
    const tokenHash = queryParams.get('token_hash') || fragParams.get('token_hash');
    const otpType   = queryParams.get('type')       || fragParams.get('type');
    if (tokenHash && otpType) {
      supabase.auth.verifyOtp({ type: otpType, token_hash: tokenHash })
        .then(({ error }) => {
          clearTimeout(timeout);
          if (error) {
            finish(false,
              `Couldn't verify the invite: ${error.message}. `
              + 'Ask your firm to send a new invite link.'
            );
          } else {
            finish(true);
          }
        })
        .catch((err) => {
          clearTimeout(timeout);
          finish(false, `Network error verifying invite: ${err?.message || 'unknown'}.`);
        });
      return () => clearTimeout(timeout);
    }

    // ── (1) + (2) detectSessionInUrl handles the hash/code itself ──
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) { clearTimeout(timeout); finish(true); return; }

      const listener = supabase.auth.onAuthStateChange((event, sess) => {
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && sess) {
          clearTimeout(timeout);
          finish(true);
        } else if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          clearTimeout(timeout);
          finish(false,
            'This invite link is no longer valid. Ask your firm to send you a new one.'
          );
        }
      });
      subscription = listener.data.subscription;
    }).catch((err) => {
      clearTimeout(timeout);
      finish(false,
        `We couldn't reach the authentication service: ${err?.message || 'network error'}. `
        + 'Check your connection and try again.'
      );
    });

    return () => {
      clearTimeout(timeout);
      if (subscription) subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw.length < 8)  { setError('Password must be at least 8 characters.'); return; }
    if (pw !== confirm) { setError('Passwords do not match.'); return; }
    setSaving(true);
    try {
      await completePasswordSetup(pw);
      setDone(true);
    } catch (err) {
      setError(err.message || 'Failed to set password. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/logo.png" alt="DocketFlow" className="mx-auto h-28 w-auto drop-shadow-xl mb-3" />
          <p className="text-blue-100 text-sm">Manage Cases. Empower Justice.</p>
        </div>
        <div className="bg-white rounded-lg shadow-xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <KeyRound size={20} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Set your password</h2>
              <p className="text-xs text-gray-500">One last step — choose a password to access your account.</p>
            </div>
          </div>

          {done ? (
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
              <p className="text-green-700 text-sm font-medium">Password set! Taking you to the app…</p>
            </div>
          ) : verifyError ? (
            // Invite-link failure — distinct UI from the "your password
            // is wrong" path. Gives the user a real recovery action
            // (request a new invite) instead of a dead spinner.
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm leading-relaxed">{verifyError}</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => window.location.reload()}
                  className="flex-1 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium transition"
                >
                  Try again
                </button>
                <a
                  href="mailto:support@docketflowapp.com?subject=Portal invite link not working"
                  className="flex-1 inline-flex items-center justify-center px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium transition"
                >
                  Email support
                </a>
              </div>
              <p className="text-xs text-slate-500 text-center pt-1">
                Or contact your firm directly to send a new invite.
              </p>
            </div>
          ) : !sessionReady ? (
            <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Loader size={18} className="animate-spin text-blue-500 flex-shrink-0" />
              <p className="text-blue-700 text-sm">Verifying your invite link…</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Min. 8 characters"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Repeat password"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              {error && (
                <div className="p-3 bg-red-50 border border-red-300 rounded-lg flex gap-2">
                  <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-blue-600 text-white py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition font-medium flex items-center justify-center gap-2"
              >
                {saving && <Loader size={16} className="animate-spin" />}
                Set password & continue
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-blue-100 text-sm mt-6">© Phoenix Tech · DocketFlow</p>
      </div>
    </div>
  );
}

// Capture ?invite=CODE from the URL immediately — before any renders —
// so it works whether the user is logged in or not.
function captureInviteCode() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('invite');
  if (code) {
    sessionStorage.setItem('inviteCode', code);
    window.history.replaceState({}, '', window.location.pathname);
  }
}
captureInviteCode();

export default function AppWrapper() {
  const { isAuthenticated, loading, loginInProgress, userProfile, refreshProfile, needsPasswordSetup, mustChangePassword, logout } = useAuth();
  const { impersonating, loading: impersonationLoading } = useImpersonation();
  const [joiningFirm,   setJoiningFirm]   = useState(false);
  const [joinError,     setJoinError]      = useState('');
  const [isPortalClient, setIsPortalClient] = useState(false);
  const [portalChecked,  setPortalChecked]  = useState(false);

  // If an authenticated user lands on an invite link and they have no firm yet,
  // process the invite code immediately instead of waiting for FirmContext.
  useEffect(() => {
    const code = sessionStorage.getItem('inviteCode');
    if (!isAuthenticated || !code) return;
    if (userProfile?.firm_id) {
      // Already in a firm — clear the stale code and show a message.
      sessionStorage.removeItem('inviteCode');
      setJoinError('You are already a member of a firm and cannot join another.');
      return;
    }

    setJoiningFirm(true);
    joinFirmByInviteCode(code).then(({ error }) => {
      sessionStorage.removeItem('inviteCode');
      if (error) {
        setJoinError(error.message || 'Invalid invite link.');
      } else if (refreshProfile) {
        refreshProfile(); // re-fetch profile so FirmContext picks up the new firm_id
      }
      setJoiningFirm(false);
    });
  }, [isAuthenticated, userProfile, refreshProfile]);

  // Determine whether the authenticated user is a portal client or staff.
  // Portal clients are identified by contacts.portal_user_id = auth.uid()
  // and have portal:true in their auth user_metadata. They must NOT be routed
  // to the staff App even if a public.users row was erroneously created for them.
  //
  // Order of checks (most authoritative first):
  //   1. contacts.portal_user_id = auth.uid()   — source of truth, beats everything
  //   2. user.user_metadata.portal === true     — fast path for fresh invites
  //   3. userProfile present                    — only NOW assume staff
  //
  // Old order (metadata → userProfile → contact) broke when the invite-portal-client
  // edge function fell back to magic-link for already-registered emails (no metadata
  // is carried on magic-links), so a portal client with an old public.users row
  // was routed to <App /> and silently sent through firm onboarding.
  useEffect(() => {
    if (!isAuthenticated || loading) return;

    (async () => {
      // 1. Authoritative check: does a contact row claim this auth user?
      try {
        const { data: contact } = await fetchPortalContact();
        if (contact) {
          setIsPortalClient(true);
          setPortalChecked(true);
          return;
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('[AppWrapper] fetchPortalContact threw, falling back:', err);
        }
      }

      // 2. Fast path: metadata flag from the invite-portal-client edge function
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.user_metadata?.portal === true) {
        setIsPortalClient(true);
        setPortalChecked(true);
        return;
      }

      // 3. Otherwise — staff user if a userProfile row exists
      if (userProfile) {
        setIsPortalClient(false);
        setPortalChecked(true);
        return;
      }

      // No userProfile, no portal flag, no contact link — access-not-found.
      setIsPortalClient(false);
      setPortalChecked(true);
    })();
  }, [isAuthenticated, loading, userProfile]);

  if (loading || loginInProgress || joiningFirm || (isAuthenticated && !portalChecked)) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100 dark:bg-gray-900 gap-3">
        <Loader size={40} className="animate-spin text-blue-600" />
        <p className="text-sm text-gray-500">
          {joiningFirm ? 'Joining firm…' : 'Connecting…'}
        </p>
        <p className="text-xs text-gray-400 max-w-xs text-center">
          First load may take up to 30 seconds if the server is waking up.
        </p>
      </div>
    );
  }

  if (joinError) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 max-w-sm w-full text-center space-y-4">
          <p className="text-red-600 font-medium">{joinError}</p>
          <button
            onClick={() => setJoinError('')}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
          >
            Continue to app
          </button>
        </div>
      </div>
    );
  }

  // Invited user who clicked their link — must set a password before entering the app
  if (needsPasswordSetup) return <SetPasswordScreen />;

  // Public intake form — accessible without authentication
  const publicIntakeId = getPublicIntakeId();
  if (publicIntakeId) return <PublicIntakeForm formId={publicIntakeId} />;

  // Public invoice view — accessible without authentication via share token
  const publicInvoiceToken = getPublicInvoiceToken();
  if (publicInvoiceToken) return <InvoicePublicPage token={publicInvoiceToken} />;

  // Client portal login / invite landing — show before auth check so unauthenticated clients can sign in
  if (!isAuthenticated && isPortalLoginRoute()) return <PortalLoginPage />;

  // Unauthenticated visitor — pick the right surface based on the hash.
  // The marketing surface (home, about, book-demo) wraps in MarketingNav
  // + MarketingFooter; the login surface uses the existing LoginPage,
  // which has its own full-page chrome.
  if (!isAuthenticated) return <MarketingRouter />;

  // Deactivated employee — block access and prompt to contact their administrator.
  // Only applies to users created through the employee-access flow (they have
  // employee_id set). Firm owners who registered normally are never blocked here.
  if (userProfile?.employee_id && userProfile?.status === 'inactive') {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl p-8 max-w-sm w-full text-center space-y-5 border border-slate-200 dark:border-slate-800">
          <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto">
            <UserX size={26} className="text-red-500" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Account deactivated</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
              Your account has been deactivated. Please contact your firm administrator to restore access.
            </p>
          </div>
          <button
            onClick={logout}
            className="w-full px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-sm font-medium transition"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Employee first-time login — force password change before entering the app
  if (mustChangePassword) return <ChangePasswordPage />;

  if (isPortalClient) return <PortalApp />;

  // Safety net: authenticated but no firm profile and not a portal client
  // — this happens when a portal client logs in before migration 047 runs,
  //   or if the contact lookup failed. Sign them out and send to portal login.
  if (!userProfile) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-sm w-full text-center space-y-4">
          <div className="text-4xl">🔒</div>
          <h2 className="text-lg font-semibold text-slate-900">Access not found</h2>
          <p className="text-sm text-slate-500">
            Your account isn't linked to this portal yet. Please use the login link sent to your email, or contact your firm.
          </p>
          <button
            onClick={logout}
            className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition"
          >
            Back to portal login
          </button>
        </div>
      </div>
    );
  }

  // Super-admin → super-admin console, unless they're actively impersonating
  // a firm (in which case render the tenant app for that firm). Impersonation
  // state is authoritative on the server (super_admin_impersonations).
  if (userProfile?.is_super_admin) {
    if (impersonationLoading) return null;
    if (!impersonating) return <SuperAdminApp />;
  }
  return <App />;
}
